import React from 'react';
import ReactDOM from 'react-dom';
import { Box, CircularProgress, LinearProgress, Typography } from '@mui/material';

/**
 * Composant LoadingOverlay réutilisable
 * Affiche un overlay de chargement avec Portal pour éviter le flickering
 * 
 * @param {boolean} isOpen - Contrôle si l'overlay est visible
 * @param {string} title - Titre principal (texte déjà traduit)
 * @param {string} subtitle - Sous-titre (texte déjà traduit)
 * @param {string} warning - Texte d'avertissement (texte déjà traduit)
 * @param {string} primaryColor - Couleur principale (optionnel, défaut: '#6200ee')
 * @param {string} accentColor - Couleur d'accent pour le gradient (optionnel, défaut: '#03dac4')
 * @param {number} circleSize - Taille du CircularProgress en pixels (optionnel, défaut: 70)
 * @returns {JSX.Element|null}
 */
const LoadingOverlay = ({
  isOpen = false,
  title = 'Loading...',
  subtitle = 'Please wait...',
  warning = 'Do not close this page...',
  primaryColor = '#6200ee',
  accentColor = '#03dac4',
  circleSize = 70,
  showWarning = true
}) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(25px)',
        WebkitBackdropFilter: 'blur(25px)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        animation: 'fadeIn 0.3s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 }
        }
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 3,
          padding: 5,
          boxShadow: `0 8px 40px ${primaryColor}40`,
          textAlign: 'center',
          minWidth: 350,
          maxWidth: 500,
          border: `2px solid ${primaryColor}`
        }}
      >
        <CircularProgress 
          size={circleSize} 
          thickness={4} 
          sx={{
            color: primaryColor,
            mb: 3
          }}
        />
        
        <Box mt={3}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{
              background: `linear-gradient(45deg, ${primaryColor} 30%, ${accentColor} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
              mb: 2
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 3, mt: 2, fontSize: '0.95rem' }}
          >
            {subtitle}
          </Typography>
          
          <LinearProgress 
            sx={{ 
              width: '100%',
              height: 8,
              borderRadius: 4,
              backgroundColor: `${primaryColor}20`,
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(45deg, ${primaryColor} 30%, ${accentColor} 90%)`,
                borderRadius: 4
              }
            }} 
          />
          
          {showWarning && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                mt: 3, 
                display: 'block', 
                fontStyle: 'italic',
                fontSize: '0.85rem'
              }}
            >
              ⚠️ {warning}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>,
    document.body
  );
};

export default LoadingOverlay;
